import {
	type IWorkfrontValueProposal,
	WORKFRONT_TARGETS,
	WorkfrontMappingKind,
	WorkfrontValueMatch,
} from '@atlas/types'
import { CardShell, FieldShell, SectionCard } from '@atlas/ui-components'
import { Button, Checkbox, Input } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBootstrapValues } from '@/hooks/useClassification'
import { useValueMappings, useValueProposals } from '@/hooks/useWorkfrontMappings'
import type { ValueListRow, ValueRow } from '@/services/workfront-mappings.service'
import { EnumFilter } from '../shared'
import { ProposalsDialog } from './components/ProposalsDialog'
import { ValuesTable } from './components/ValuesTable'
import { useDrawerContext, useReportLoadFailure, useSelection } from './tab-support'
import { NEW_ROW_KEY, useFixLink, useMappingEditor } from './useMappingEditor'

/** The axes a field mapping can write, so the axes whose labels are mapped. */
const MAPPED_AXES = [...new Set(WORKFRONT_TARGETS.flatMap((target) => (target.axis ? [target.axis] : [])))]

const sameLabel = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

const valueRowOf = (row: Pick<ValueListRow, 'axis' | 'label' | 'valueId' | 'geoValueId'>): ValueRow => ({
	axis: row.axis,
	label: row.label,
	valueId: row.valueId ?? '',
	geoValueId: row.geoValueId,
})

/**
 * Values: each Workfront label of a mapped axis and the ATLAS value it resolves to.
 * Unmatched labels hold their objects until mapped. Proposals are accepted after
 * review; several labels can be mapped to one value. `?axis=…&label=…&edit=1`
 * (an `UNKNOWN_VALUE`'s Fix) opens that label's drawer.
 */
export function ValuesTab() {
	const [params] = useSearchParams()
	const [axis, setAxis] = useState<string | undefined>(params.get('axis') ?? undefined)
	const [match, setMatch] = useState<WorkfrontValueMatch | undefined>()
	const [waiting, setWaiting] = useState(false)
	const [search, setSearch] = useState(params.get('label') ?? '')
	const [reviewing, setReviewing] = useState(false)
	const query = useValueMappings({ axis, match, waiting })
	const rows = query.data ?? []
	const proposals = useValueProposals(reviewing ? axis : undefined)
	const selection = useSelection()
	const editor = useMappingEditor(WorkfrontMappingKind.VALUE, (key) => {
		const row = rows.find((r) => r.key === key)
		return row ? valueRowOf(row) : undefined
	})
	const editedAxis = (editor.editing?.row as ValueRow | undefined)?.axis
	const context = useDrawerContext(editedAxis, (editor.editing?.row as ValueRow | undefined)?.label)
	const geographies = useBootstrapValues('geography')
	useReportLoadFailure(query.isError, 'values')

	const edit = (row: ValueListRow) =>
		editor.open({ key: row.key, row: valueRowOf(row), title: `Map "${row.label}"`, canDelete: false })
	useFixLink(query.isSuccess, (fix) => {
		const label = fix.get('label') ?? ''
		const found = rows.find((r) => sameLabel(r.label, label))
		const row = found ?? { key: NEW_ROW_KEY, axis: fix.get('axis') ?? '', label, valueId: null, geoValueId: null }
		editor.open({ key: row.key, row: valueRowOf(row), title: `Map "${label}"`, fromFix: true })
	})

	const shown = rows.filter((row) => !search.trim() || row.label.toLowerCase().includes(search.trim().toLowerCase()))
	const chosen = rows.filter((row) => selection.selected.has(row.key))
	const mapSeveral = () => {
		const [first] = chosen
		if (!first) return
		editor.open({
			key: first.key,
			row: valueRowOf({ ...first, valueId: null }),
			title: `Map ${chosen.length} Workfront labels`,
			several: chosen.map(({ key, label }) => ({ key, label })),
		})
		selection.clear()
	}
	const accept = (accepted: IWorkfrontValueProposal[]) =>
		editor.saveMany(
			accepted.map((p) => ({
				key: rows.find((r) => r.axis === p.axis && sameLabel(r.label, p.label))?.key ?? NEW_ROW_KEY,
				row: { axis: p.axis, label: p.label, valueId: p.valueId, geoValueId: null },
				valueLabel: p.valueLabel,
			})),
			`Draft saved: ${accepted.length} proposals accepted`,
			() => setReviewing(false),
		)
	const sameAxis = chosen.length > 0 && chosen.every((row) => row.axis === chosen[0]?.axis)

	return (
		<div className="space-y-4">
			<SectionCard title="Filters">
				<div className="grid items-end gap-4 md:grid-cols-4">
					<EnumFilter label="Axis" values={MAPPED_AXES} value={axis} onChange={setAxis} />
					<EnumFilter label="Match" values={Object.values(WorkfrontValueMatch)} value={match} onChange={setMatch} />
					<FieldShell label="Workfront label" htmlFor="values-search">
						<Input id="values-search" value={search} onChange={(event) => setSearch(event.target.value)} />
					</FieldShell>
					<Checkbox isSelected={waiting} onChange={setWaiting}>
						Objects waiting
					</Checkbox>
				</div>
			</SectionCard>
			<div className="flex flex-wrap items-center gap-2">
				<Button size="sm" isDisabled={!axis} onPress={() => setReviewing(true)}>
					Accept proposals
				</Button>
				<Button size="sm" variant="outline" isDisabled={!sameAxis} onPress={mapSeveral}>
					Map selected to one value
				</Button>
				{!axis ? <span className="text-ds-xs text-ds-text-muted">Choose an axis to review its proposals.</span> : null}
			</div>
			<CardShell>
				<ValuesTable
					rows={shown}
					filtered={!!(axis || match || waiting || search)}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					selected={selection.selected}
					onToggle={selection.onToggle}
					geographyLabel={(id) => geographies.find((value) => value.valueId === id)?.label}
					onEdit={edit}
				/>
			</CardShell>
			<ProposalsDialog
				open={reviewing}
				axisLabel={axis ?? ''}
				proposals={proposals.data ?? null}
				submitting={editor.saving}
				onCancel={() => setReviewing(false)}
				onAccept={accept}
			/>
			{editor.drawer(context)}
		</div>
	)
}
